import React from 'react';
import { addYears, differenceInCalendarDays, endOfWeek, format, parseISO, setYear, startOfWeek } from 'date-fns';
import { ArrowRightIcon, CalendarDaysIcon, FolderOpenIcon, PartyPopperIcon, PlaneIcon, UserRoundIcon, UsersIcon, WalletIcon } from 'lucide-react';
import { PtoBalanceCard } from '../../components/self/PtoBalanceCard';
import { RequestStatusBadge } from '../../components/self/RequestStatusBadge';
import { Avatar } from '../../components/ui/Avatar';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { EmptyState } from '../../components/ui/EmptyState';
import { timeOffEntries } from '../../data/timeOff';
import { useSelfService } from '../../hooks/useSelfService';
import type { Navigate, Route } from '../../types/hr';
import type { IconComponent } from '../../types/ui';
import { displayName } from '../../utils/employees';
import { TODAY, formatDays, formatRange } from '../../utils/format';
import { upcomingHolidays } from '../../utils/holidays';
import { teamFor } from '../../utils/teams';
import { overlaps, policyName } from '../../utils/timeOff';
interface MyHomeProps {
  onNavigate: Navigate;
  onRequestTimeOff: () => void;
}
const TODAY_ISO = format(TODAY, 'yyyy-MM-dd');
const QUICK_LINKS: {
  label: string;
  description: string;
  icon: IconComponent;
  route: Route;
}[] = [{
  label: 'My Info',
  description: 'Personal & job details',
  icon: UserRoundIcon,
  route: {
    page: 'my-info'
  }
}, {
  label: 'My Pay',
  description: 'Pay rate & schedule',
  icon: WalletIcon,
  route: {
    page: 'my-pay'
  }
}, {
  label: 'My Documents',
  description: 'Agreements & forms',
  icon: FolderOpenIcon,
  route: {
    page: 'my-documents'
  }
}, {
  label: 'Time Off',
  description: 'Balance & requests',
  icon: CalendarDaysIcon,
  route: {
    page: 'my-timeoff'
  }
}];
export function MyHome({
  onNavigate,
  onRequestTimeOff
}: MyHomeProps) {
  const {
    me,
    upcoming,
    holidays,
    employees,
    allRequests
  } = useSelfService();
  const team = teamFor(me);
  const hire = parseISO(me.hireDate);
  let anniversary = setYear(hire, TODAY.getFullYear());
  if (anniversary < TODAY) anniversary = addYears(anniversary, 1);
  const daysToAnniversary = differenceInCalendarDays(anniversary, TODAY);
  const anniversaryYears = anniversary.getFullYear() - hire.getFullYear();
  const weekStart = startOfWeek(TODAY, {
    weekStartsOn: 1
  });
  const weekEnd = endOfWeek(TODAY, {
    weekStartsOn: 1
  });
  const teammates = employees.filter(e => e.id !== me.id && e.department === me.department && teamFor(e) === team && e.status !== 'Terminated');
  const ranges = [...timeOffEntries.map(e => ({
    employeeId: e.employeeId,
    start: e.start,
    end: e.end
  })), ...allRequests.filter(r => r.status === 'approved').map(r => ({
    employeeId: r.employeeId,
    start: r.start,
    end: r.end
  }))];
  const outThisWeek = teammates.map(person => ({
    person,
    range: ranges.find(r => r.employeeId === person.id && overlaps(r, weekStart, weekEnd))
  })).filter((x): x is {
    person: typeof teammates[number];
    range: {
      employeeId: string;
      start: string;
      end: string;
    };
  } => Boolean(x.range));
  const nextHolidays = upcomingHolidays(holidays, TODAY_ISO, 3);
  return <div data-id="element-1624" data-mp="xw398cqa77q2">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between" data-id="element-1625" data-mp="xtgzc6fx4g4o">
        <div data-id="element-1626" data-mp="12sacfxram54b">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-azure" data-id="element-1627" data-mp="56midjib7yvy">{format(TODAY, 'EEEE, MMMM d, yyyy')}</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-navy md:text-[28px]" data-id="element-1628" data-mp="a4xrfs0s65d4">Good morning, {me.preferredName || me.firstName}</h1>
          <p className="mt-1.5 text-sm text-muted" data-id="element-1629" data-mp="f3hbtnzq793d">
            {me.jobTitle} · {me.department} · {team} team
          </p>
        </div>
        <div className="inline-flex items-center gap-3 self-start rounded-2xl bg-white px-4 py-3 shadow-soft ring-1 ring-slate-900/[0.04] md:self-auto" data-id="element-1630" data-mp="3ilshu80e5xab">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-flame/10 text-flame" data-id="element-1631" data-mp="3nird4tvskaa3">
            <PartyPopperIcon className="h-5 w-5" aria-hidden="true" data-id="element-1632" data-mp="1t4ncd3hq00jr" />
          </span>
          <span data-id="element-1633" data-mp="1p4d4tmea522s">
            <span className="block text-sm font-semibold text-navy" data-id="element-1634" data-mp="1p0pawm4xadnj">
              {daysToAnniversary === 0 ? `Happy ${anniversaryYears}-year anniversary!` : `${daysToAnniversary} days to your ${anniversaryYears}-year anniversary`}
            </span>
            <span className="block text-xs text-muted" data-id="element-1635" data-mp="2crl8jzw2832p">{format(anniversary, 'MMMM d, yyyy')}</span>
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3" data-id="element-1636" data-mp="27t04fpdosz8z">
        <div className="space-y-6 xl:col-span-2" data-id="element-1637" data-mp="2lrzvf3oo9q8b">
          <PtoBalanceCard onRequest={onRequestTimeOff} data-id="element-1638" data-mp="2lqor92rbeggo" />

          <Card title="My upcoming time off" subtitle={upcoming.length ? `${upcoming.length} upcoming` : undefined} icon={PlaneIcon} action={<Button variant="ghost" size="sm" onClick={() => onNavigate({
          page: 'my-timeoff',
          tab: 'requests'
        })} data-id="element-1640" data-mp="mh05k94r9ce9">
                All requests
              </Button>} bodyClassName="px-3 pb-3" data-id="element-1639" data-mp="2gv9hon0cb6q2">
            {upcoming.length === 0 ? <div className="px-3 pb-3" data-id="element-1641" data-mp="2pbi7sbh26cke">
                <EmptyState icon={PlaneIcon} title="Nothing scheduled" description="Plan a break — your request goes straight to your manager." compact action={<Button size="sm" variant="primary" onClick={onRequestTimeOff} data-id="element-1643" data-mp="2kbm0a295k303">
                      Request Time Off
                    </Button>} data-id="element-1642" data-mp="2pague0ckldzi" />
              </div> : <ul className="space-y-0.5" data-id="element-1644" data-mp="2aer43q0cqeoz">
                {upcoming.map(r => <li key={r.id} className="flex items-center gap-4 rounded-xl px-3 py-3 hover:bg-canvas/70" data-id="element-1645" data-mp="4khqq32k5r2p">
                    <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl bg-navy/[0.05] text-navy" data-id="element-1646" data-mp="8lbduu86f1uj">
                      <span className="text-[10px] font-semibold uppercase leading-none" data-id="element-1647" data-mp="1hkkv0dyrcjvd">{format(parseISO(r.start), 'MMM')}</span>
                      <span className="text-lg font-semibold leading-tight" data-id="element-1648" data-mp="18ibnzy2p78mq">{format(parseISO(r.start), 'd')}</span>
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-1649" data-mp="13ktoeyakm5re">
                      <p className="text-sm font-medium text-navy" data-id="element-1650" data-mp="209ri0d6l3xmz">{formatRange(r.start, r.end)}</p>
                      <p className="text-xs text-muted" data-id="element-1651" data-mp="1w85xq6rq0h2r">
                        {policyName(r.policyId)} · {formatDays(r.days)}
                      </p>
                    </div>
                    <RequestStatusBadge status={r.status} data-id="element-1652" data-mp="1rfcy1xb3zyxj" />
                  </li>)}
              </ul>}
          </Card>
        </div>

        <div className="space-y-6" data-id="element-1653" data-mp="2jyjugal9343o">
          <Card title="Upcoming holidays" icon={PartyPopperIcon} action={<Button variant="ghost" size="sm" onClick={() => onNavigate({
          page: 'holidays'
        })} data-id="element-1655" data-mp="2mi6fay8ha04m">
                Calendar
              </Button>} bodyClassName="px-3 pb-3" data-id="element-1654" data-mp="2jzuuq0fhltq1">
            <ul className="space-y-0.5" data-id="element-1656" data-mp="2wgm6f17i99e4">
              {nextHolidays.map(h => <li key={h.id} className="flex items-center gap-3 rounded-xl px-3 py-2.5" data-id="element-1657" data-mp="2wiqjo9rankr2">
                  <div className={`flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-xl ${h.kind === 'Company' ? 'bg-flame/10 text-[#c25a00]' : 'bg-navy/[0.05] text-navy'}`} data-id="element-1658" data-mp="30fmm4auorvww">
                    <span className="text-[10px] font-semibold uppercase leading-none" data-id="element-1659" data-mp="3ae2dmexml76x">{format(parseISO(h.date), 'MMM')}</span>
                    <span className="text-base font-semibold leading-tight" data-id="element-1660" data-mp="3k5owmxg7gak8">{format(parseISO(h.date), 'd')}</span>
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-1661" data-mp="3o8v0jyfuhpnj">
                    <p className="truncate text-sm font-medium text-navy" data-id="element-1662" data-mp="3t6mt8uobdfss">{h.name}</p>
                    <p className="text-xs text-muted" data-id="element-1663" data-mp="3t4ac2jqdr9gs">
                      {format(parseISO(h.date), 'EEEE')}
                      {h.halfDay ? ' · half day' : ''}
                    </p>
                  </div>
                  <Badge tone={h.kind === 'Company' ? 'orange' : 'navy'} data-id="element-1664" data-mp="sad8nu3j89f4">{h.kind}</Badge>
                </li>)}
            </ul>
          </Card>

          <Card title="Out on my team this week" subtitle={`${team} · ${format(weekStart, 'MMM d')} – ${format(weekEnd, 'MMM d')}`} icon={UsersIcon} bodyClassName="px-3 pb-3" data-id="element-1665" data-mp="nbtn3usuozna">
            {outThisWeek.length === 0 ? <div className="px-3 pb-3" data-id="element-1666" data-mp="3qpwgn1mrx1nc">
                <EmptyState icon={UsersIcon} title="Everyone's in this week" compact data-id="element-1667" data-mp="9br8qijr4ocs" />
              </div> : <ul className="space-y-0.5" data-id="element-1668" data-mp="4k0wg7197qiq">
                {outThisWeek.map(({
              person,
              range
            }) => <li key={person.id} className="flex items-center gap-3 rounded-xl px-3 py-2" data-id="element-1669" data-mp="3cz1cg2n7z8q3">
                    <Avatar name={displayName(person)} seed={person.id} size="sm" data-id="element-1670" data-mp="380zuxm5fzhgq" />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-navy" data-id="element-1671" data-mp="1nifju0fcda6p">{displayName(person)}</span>
                    <span className="text-xs text-muted" data-id="element-1672" data-mp="1sbrvl9yx9mi1">{formatRange(range.start, range.end)}</span>
                  </li>)}
              </ul>}
          </Card>

          <section aria-label="Quick links" className="grid grid-cols-2 gap-3" data-id="element-1673" data-mp="1il5ns1xg609w">
            {QUICK_LINKS.map(({
            label,
            description,
            icon: Icon,
            route
          }) => <button key={label} type="button" onClick={() => onNavigate(route)} className="group flex flex-col rounded-2xl bg-white p-4 text-left shadow-soft ring-1 ring-slate-900/[0.04] transition hover:-translate-y-0.5 hover:shadow-lift" data-id="element-1674" data-mp="2b8t6fbf5qgtw">
                <span className="flex items-center justify-between" data-id="element-1675" data-mp="lt20svlrukcs">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-azure/10 text-azure" data-id="element-1676" data-mp="pvglll903u6p">
                    <Icon className="h-[18px] w-[18px]" aria-hidden="true" data-id="element-1677" data-mp="1ozjjw0u09i7p" />
                  </span>
                  <ArrowRightIcon className="h-4 w-4 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-flame" aria-hidden="true" data-id="element-1678" data-mp="1tzo2bweh4n0k" />
                </span>
                <span className="mt-3 text-sm font-semibold text-navy" data-id="element-1679" data-mp="1k9bg7zr0glsb">{label}</span>
                <span className="text-xs text-muted" data-id="element-1680" data-mp="1k6yq2zb3n546">{description}</span>
              </button>)}
          </section>
        </div>
      </div>
    </div>;
}